<script setup lang="ts">
import { RouterView, useRoute } from 'vue-router'
import { computed, watch, ref, onMounted, onUnmounted } from 'vue'
import { NConfigProvider, NNotificationProvider, NDialogProvider, NMessageProvider, NGlobalStyle } from 'naive-ui'
import { authService } from '@/services/authService'
import { useCartInit } from '@/composables/useCartInit'
import { logger } from '@/utils/logger'

const route = useRoute()
const routerKey = ref(0)

// 固定使用亮色主題
const naiveTheme = null

// 購物車初始化管理
const { initializeCartIfNeeded, watchAuthState } = useCartInit()

// 根據路由動態計算 App 的佈局 class
const appLayoutClass = computed(() => {
  const path = route.path
  
  if (path.startsWith('/admin') || path.startsWith('/sample')) {
    return 'layout-admin'
  } else if (path === '/dashboard') {
    return 'layout-frontend'
  } else if (path === '/') {
    return 'layout-home'
  }
  
  return 'layout-frontend'
})

// 監聽路由變化，動態設定 #app 的 class
watch(appLayoutClass, (newClass) => {
  const app = document.getElementById('app')
  if (app) {
    // 清除所有佈局相關的 class
    app.classList.remove('layout-home', 'layout-frontend', 'layout-admin')
    // 添加新的 class
    app.classList.add(newClass)
  }
}, { immediate: true })

// 移除方向控制功能以避免對輸入框的干擾
// const setupOrientationControl = () => {
//   // 功能已移除
//   return () => {}
// }

// 監聽使用者角色變更，強制重新渲染組件
const handleUserUpdated = () => {
  // 只在不是回調頁面時才重新渲染
  if (!window.location.pathname.includes('/line/callback')) {
    routerKey.value++
    logger.debug('User role updated, forcing component re-render')
  } else {
    logger.debug('User role updated, skipping re-render (on callback page)')
  }
}

let cleanupCartWatcher: (() => void) | null = null

onMounted(async () => {
  window.addEventListener('userUpdated', handleUserUpdated)
  
  // 初始化購物車監聽
  cleanupCartWatcher = watchAuthState()
  
  // 初始檢查是否需要載入購物車
  await initializeCartIfNeeded()
})

onUnmounted(() => {
  window.removeEventListener('userUpdated', handleUserUpdated)
  if (cleanupCartWatcher) {
    cleanupCartWatcher()
  }
})
</script>

<template>
  <n-config-provider :theme="naiveTheme">
    <n-notification-provider>
      <n-dialog-provider>
        <n-message-provider>
          <!-- 橫向提示已移除 -->
          
          <!-- 主要應用內容 -->
          <RouterView :key="routerKey" />
          
          <!-- 全域樣式 (只在 App.vue 中使用一次) -->
          <n-global-style />
        </n-message-provider>
      </n-dialog-provider>
    </n-notification-provider>
  </n-config-provider>
</template>
